/**
 * Tab-scoped per-job UI preference (sessionStorage).
 *
 * - Keyed by Auth0 `user.sub` + `jobId` so profiles and jobs never share values.
 * - Nothing is read or written while `user.sub` is unknown; `fallback` applies.
 * - Stored values outside `allowed` are cleared and ignored.
 * - Resolved during render when user or job changes, so the previous job's
 *   value is never rendered for the next one.
 */

import { useCallback, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

function storageKey(namespace: string, userKey: string, jobId: string): string {
  return `${namespace}:${userKey}:${jobId}`;
}

export function readUserJobPreference<T extends string>(
  namespace: string,
  userKey: string | null,
  jobId: string,
  allowed: readonly T[]
): T | null {
  if (!userKey || !jobId) return null;
  const key = storageKey(namespace, userKey, jobId);
  try {
    const stored = sessionStorage.getItem(key);
    if (stored == null) return null;
    if ((allowed as readonly string[]).includes(stored)) return stored as T;
    sessionStorage.removeItem(key);
  } catch {
    // storage unavailable (private mode / disabled)
  }
  return null;
}

export function writeUserJobPreference(
  namespace: string,
  userKey: string | null,
  jobId: string,
  value: string
): void {
  if (!userKey || !jobId) return;
  try {
    sessionStorage.setItem(storageKey(namespace, userKey, jobId), value);
  } catch {
    // ignore quota / private-mode failures
  }
}

export interface UserJobPreferenceSnapshot<T extends string> {
  scope: string;
  value: T;
}

/** Returns `snapshot` unchanged while it still belongs to this user + job. */
export function resolveUserJobPreference<T extends string>(
  snapshot: UserJobPreferenceSnapshot<T> | null,
  namespace: string,
  userKey: string | null,
  jobId: string,
  allowed: readonly T[],
  fallback: T
): UserJobPreferenceSnapshot<T> {
  const scope = storageKey(namespace, userKey ?? '', jobId);
  if (snapshot?.scope === scope) return snapshot;
  return {
    scope,
    value: readUserJobPreference(namespace, userKey, jobId, allowed) ?? fallback,
  };
}

export function useUserJobSessionPreference<T extends string>(
  namespace: string,
  jobId: string,
  allowed: readonly T[],
  fallback: T
) {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const userKey =
    isAuthenticated && !isLoading && typeof user?.sub === 'string' && user.sub
      ? user.sub
      : null;

  const [snapshot, setSnapshot] = useState(() =>
    resolveUserJobPreference<T>(null, namespace, userKey, jobId, allowed, fallback)
  );
  const current = resolveUserJobPreference(
    snapshot,
    namespace,
    userKey,
    jobId,
    allowed,
    fallback
  );
  if (current !== snapshot) setSnapshot(current);

  const setValue = useCallback(
    (next: T) => {
      setSnapshot({ scope: storageKey(namespace, userKey ?? '', jobId), value: next });
      writeUserJobPreference(namespace, userKey, jobId, next);
    },
    [namespace, userKey, jobId]
  );

  return { value: current.value, setValue };
}
